import {
	type AutocompleteReshapeSourcesBySourceId,
	createAutocomplete,
} from '@algolia/autocomplete-core'
import { useCallback, useMemo, useRef, useState } from 'react'

import logger from '@/client/utils/logger'
import { AUTOCOMPLETE_CONFIG } from '@/constants/autocomplete'
import useAiSearchStore from '@/stores/aiSearchStore'
import {
	isKeywordsSearchMode,
	isLyricsSearchMode,
	isPromptSearchMode,
	isReferenceSearchMode,
} from '@/stores/searchSelectorStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'
import {
	createGuardedAutocompleteFetch,
	fetchAutocomplete,
} from '@/utils/algolia/autocomplete'
import { createDebouncedQuery } from '@/utils/algolia/debounceAlgoliaQuery'

import isSupportedStreamingServiceLink from '../../audio-search/isSupportedStreamingServiceLink'
import type {
	AutocompleteState,
	UseCoreAutocompleteProps,
	UseCoreAutocompleteReturn,
} from '../../SearchBox.types'
import { isValidPromptSearchTerm } from '../components'
import {
	collectionsPlugin,
	emptyStatePlugin,
	facetsPlugin,
	playlistsPlugin,
	PluginHit,
	suggestionsAndPromptPlugin,
} from '../plugins'
import { getReshapedAutocompleteSources } from './reshapeAutocompleteSources'

export const useCoreAutocomplete = ({
	searchClient,
	user,
	setSearchByPrompt,
	refine,
	clearAiSearch,
	locale,
	setIndexUiState,
	refinementList,
	setSearchByAudio,
	shouldShowEmptyState,
}: UseCoreAutocompleteProps): UseCoreAutocompleteReturn => {
	const [autocompleteState, setAutocompleteState] =
		useState<AutocompleteState<PluginHit> | null>(null)
	const { prefetchedSearchByAudio } = useAiSearchStore()
	const searchType = useSearchSelectorStore((state) => state.searchType)
	const setSearchType = useSearchSelectorStore((state) => state.setSearchType)
	const setLyricsRestrictionCommitted = useSearchSelectorStore(
		(state) => state.setLyricsRestrictionCommitted
	)
	const keywordSearchMode = isKeywordsSearchMode(searchType)

	const refinementListRef = useRef(refinementList)
	refinementListRef.current = refinementList

	const executeAutocomplete = useCallback(
		(q: string) =>
			fetchAutocomplete({
				searchClient,
				query: q,
				refinementList: refinementListRef.current,
				languageCode: locale,
				user,
			}),
		[searchClient, locale, user]
	)

	const guardedFetch = useMemo(
		() =>
			createGuardedAutocompleteFetch(
				createDebouncedQuery(executeAutocomplete),
				AUTOCOMPLETE_CONFIG.MAX_CONSECUTIVE_ZERO_RESULT_REFINEMENTS
			),
		[executeAutocomplete]
	)

	const plugins = useMemo(() => {
		const emptyState = emptyStatePlugin(shouldShowEmptyState)

		const suggestionsAndPrompt = suggestionsAndPromptPlugin(
			user,
			refine,
			clearAiSearch,
			setSearchByPrompt,
			guardedFetch
		)

		const facets = facetsPlugin(setIndexUiState, refine, guardedFetch)

		const playlists = playlistsPlugin(guardedFetch)

		const collections = collectionsPlugin(guardedFetch)

		return [emptyState, suggestionsAndPrompt, facets, playlists, collections]
	}, [
		user,
		clearAiSearch,
		setSearchByPrompt,
		refine,
		setIndexUiState,
		shouldShowEmptyState,
		guardedFetch,
	])

	const autocomplete = useMemo(() => {
		return createAutocomplete({
			plugins,
			onStateChange({ state }) {
				setAutocompleteState(state as AutocompleteState<PluginHit>)
			},
			autoFocus: true,
			openOnFocus: false,
			navigator: {
				navigate({ itemUrl }) {
					const windowReference = window.open(itemUrl, '_blank')
					if (windowReference) windowReference.focus()
				},
			},
			reshape: ({ sourcesBySourceId, state }) => {
				const { query } = state

				return getReshapedAutocompleteSources({
					query,
					prefetchedSearchByAudio: prefetchedSearchByAudio ?? undefined,
					sourcesBySourceId:
						sourcesBySourceId as AutocompleteReshapeSourcesBySourceId<PluginHit>,
					isStreamingServiceLink: isSupportedStreamingServiceLink,
					keywordSearchMode,
				})
			},
			onSubmit({ state, event, setIsOpen }) {
				event.preventDefault()
				event.stopPropagation()
				const { query } = state
				const lyricsSearchMode = isLyricsSearchMode(searchType)

				const isPrefetchedAudioMatch =
					query === prefetchedSearchByAudio?.url ||
					query === prefetchedSearchByAudio?.title
				const isStreamingReference = isSupportedStreamingServiceLink(query)
				const isAudioReferenceQuery =
					isPrefetchedAudioMatch || isStreamingReference

				try {
					if (!query) {
						clearAiSearch()
						setLyricsRestrictionCommitted(false)
						useSearchSelectorStore.getState().clearInputDraft()
						refine('')
						setIsOpen(false)
						return
					}

					if (
						isPromptSearchMode(searchType) &&
						isValidPromptSearchTerm(query)
					) {
						if (user) {
							setSearchByPrompt(query)
							setIsOpen(false)
							return
						} else {
							setIsOpen(false)
						}
					}

					if (isAudioReferenceQuery) {
						if (!isReferenceSearchMode(searchType)) {
							setSearchType('reference')
						}
						setSearchByAudio(query)
						setIsOpen(false)
						return
					}

					if (isKeywordsSearchMode(searchType) || lyricsSearchMode) {
						setLyricsRestrictionCommitted(lyricsSearchMode)
						refine(query)
						clearAiSearch()
						setIsOpen(false)
						return
					}

					if (isReferenceSearchMode(searchType)) {
						if (isPrefetchedAudioMatch || isStreamingReference) {
							setSearchByAudio(query)
						} else {
							refine(query)
							clearAiSearch()
						}
						setIsOpen(false)
						return
					}

					refine(query)
					clearAiSearch()
					setIsOpen(false)
				} catch (error) {
					logger('Error in search handleSubmit', error, {
						query: query,
						searchType,
						isPrefetchedAudioMatch: String(isPrefetchedAudioMatch),
					})
				}
			},
		})
	}, [
		plugins,
		prefetchedSearchByAudio,
		setSearchByAudio,
		setSearchByPrompt,
		setSearchType,
		clearAiSearch,
		refine,
		setLyricsRestrictionCommitted,
		searchType,
		keywordSearchMode,
		user,
	])

	return {
		autocomplete,
		autocompleteState,
	}
}
